/**
 * Phase 15.6P — useRequireAdmin hook.
 *
 * Drop-in guard for /admin/* pages. Redirects to /login (anon) or /home
 * (logged-in but not admin/AR/moderator) and surfaces a toast so the user
 * understands the redirect.
 *
 * Returns {authChecked, currentUser, isAdmin}. Pages should render a
 * "Checking access…" placeholder until authChecked is true.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const ADMIN_ROLES = new Set(["admin", "ar", "moderator"]);
const ADMIN_BADGES = new Set(["admin", "founder", "moderator", "ar"]);

const isAdminUser = (u) => {
  if (!u) return false;
  if (ADMIN_ROLES.has((u.role || "").toLowerCase())) return true;
  const badges = Array.isArray(u.badges) ? u.badges : [];
  return badges.some((b) => ADMIN_BADGES.has(String(b).toLowerCase()));
};

export const useRequireAdmin = ({ allowAR = true, allowModerator = true } = {}) => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        const u = r.data?.user || r.data;
        if (cancelled) return;
        setCurrentUser(u);

        const role = (u?.role || "").toLowerCase();
        const badges = Array.isArray(u?.badges) ? u.badges.map((b) => String(b).toLowerCase()) : [];
        const hasAdminRole =
          role === "admin" ||
          (allowAR && role === "ar") ||
          (allowModerator && role === "moderator") ||
          badges.includes("admin") ||
          badges.includes("founder") ||
          (allowAR && badges.includes("ar")) ||
          (allowModerator && badges.includes("moderator"));

        if (!hasAdminRole) {
          toast.error("Admin access required");
          navigate("/home", { replace: true });
          return;
        }
        setAuthChecked(true);
      } catch (_e) {
        if (!cancelled) navigate("/login", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, allowAR, allowModerator]);

  return { authChecked, currentUser, isAdmin: authChecked };
};

export { isAdminUser };
export default useRequireAdmin;
